/* eslint-disable import/no-anonymous-default-export */
import React, { useState, useEffect, useMemo } from 'react';
import { get, size } from 'global/lib/lodash';
import Filters from './filters';
import { filterData } from './utils';
import { useFilter } from 'global/hooks/useFilter';
import { _size } from '../../../global/utils';

export default (props) => {
  const { isCourse,
    isTemplate,
    assessment,
    activeSc,
    scorecardIndex,
    assessmentLoading,
    loading,
    status,
    updateScores,
    scoredata,
    setTreedProcessedScoreData,
    processedOverallScores,
    activeEdit,
    scorecards,
    setScorecardIndex,
    searchString,
    setSearchString,
    filter,
    setFilter,
    hasSubScores,
    assessmentTasks,
  } = props;

  const filterHook = useFilter();

  const defaultFilters = useMemo(() => ({
    value: 'any',
    hideNa: !(isCourse || ['draft', 'template', 'global_template'].includes(get(assessment, 'data.status', 'draft'))),
    search: '',
    listGroup: isCourse || isTemplate ? 'category' : get(assessment, `data.scorecards.${scorecardIndex}.config.defaultDisplay`) === 'realm' ? 'location' : 'category',
    onlyLoc: false,
    useLocation: ['draft', 'template', 'global_template'].includes(get(assessment, 'data.status', 'draft')),
    assessment_status: 'active',
    useDiff: false,
    suggestedCpIds: [],
    isDefault: true,
  }), [scorecardIndex, assessment, isCourse, isTemplate]);

  const [filterLoading, setFilterLoading] = useState(assessmentLoading);
  let [filterLocal, setFilterLocal] = useState(defaultFilters);
  const [filterBtns, setFilterBtns] = useState([]);
  const [origStatus, setOrigStatus] = useState(get(assessment, 'data.status', 'draft'));
  const availableStatus = get(assessment, 'data.availableStatus');

  useEffect(() => {
    // this triggers when opening to the scores tab before opening other parts of assessments
    // (because it will assume the assessment is in "Draft" state until it's loaded)
    // and also triggers when you change assessment info while scoring (change status)
    buildFilters(true);
    if (!assessmentLoading && status !== origStatus) {
      updateScores();
      setOrigStatus(status);
    }
  }, [assessmentLoading, status, scorecardIndex, availableStatus]);

  useEffect(() => {
    // Rebuild filter buttons when tasks change (e.g. to show/hide "Has Tasks" option)
    // but do NOT reset filter values - that would be disruptive (PFM-2446)
    buildFilters(false);
  }, [assessmentTasks]);

  useEffect(() => {
    if (!loading && (!filterBtns.filter((b) => b.value === 'filterLocal.useLocation').show || _size(filterHook.user.allrealms))) {
      runFilter(scoredata);
    }
  }, [filterLocal, scoredata, searchString, loading, filterHook.user.allrealms, assessmentTasks]);

  useEffect(() => {
    props.updateStatusFilter && props.updateStatusFilter(filterLocal.assessment_status);
  }, [filterLocal.assessment_status]);

  const buildFilters = (resetValues = true) => {
    if (resetValues) {
      setFilterLocal({ ...defaultFilters, assessment_status: filterLocal.assessment_status, useDiff: filterLocal.useDiff });
    }

    const options = [
      { text: 'All Scores', val: 'any' },
      { text: 'Actionable', val: 'act' },
      (availableStatus || []).includes('target') && { text: 'Has Target', val: 'hasTarget' },
      (availableStatus || []).includes('target') && { text: 'Missed Target', val: 'missedTarget' },
      size(assessmentTasks) > 0 && { text: 'Has Tasks', val: 'hasTask' },
      { text: 'High Scores', val: 'high' },
      { text: 'Low Scores', val: 'low' },
    ].filter(f => !!f);
    
    const valLabels = get(activeSc, `config.scoreValueLabel`, []);

    if (get(activeSc, 'config.allowNA')) {
      options.push({ text: 'N/A', val: 'na' });
    }
    if (get(activeSc, 'config.allowNO')) {
      const NOLabel = valLabels && valLabels.find((i) => i.value === -1);
      options.push({ text: get(NOLabel, 'label') || 'N/O', val: -1 });
    }
    const min = parseInt(get(activeSc, 'config.minScore', '0'));
    const max = parseInt(get(activeSc, 'config.maxScore', '4'));
    const step = parseInt(get(activeSc, 'config.step', '1')) || 1;
    const range = max - min;
    [...Array(max + 1).keys()].forEach((y) => {
      if (y >= min && (y - min) % step === 0) {
        const valNormalized = range > 0 ? Math.round((y - min) / range * 100) : 0;
        const valLabel = valLabels && valLabels.find((i) => i.value === valNormalized);
        options.push({
          text: valLabel ? valLabel.label : get(activeSc, 'config.displayPercentage') ? valNormalized + '%' : y.toString(),
          val: y
        });
      }
    });

    setFilterBtns(
      hasSubScores
        ? [
          {
            val: 'listGroup',
            type: 'select',
            options: [
              { text: 'By Category', val: 'category' },
              { text: 'By Location', val: 'location' },
            ],
            onChange: v => {
              if (v === 'location') {
                setFilterLocal(f => ({ ...f, useDiff: false }))
              }
            }
          },
          { val: 'value', type: 'select', options },
          {
            text: 'Instances',
            val: 'filterLocal.onlyLoc',
            type: 'switch',
            disabled: (p) => get(p, 'selected.listGroup') === 'location',
            onChange: (val) => setFilterLocal((f) => ({ ...f, value: 'any', onlyLoc: val })),
          },
          {
            text: 'Hide N/A',
            val: 'filterLocal.hideNa',
            type: 'switch',
            disabled: (p) => get(p, 'selected.listGroup') === 'location',
            onChange: (val) => setFilterLocal((f) => ({ ...f, value: 'any', hideNa: val })),
          },
          {
            text: 'Use Location',
            val: 'filterLocal.useLocation',
            type: 'switch',
            show: (p) => {
              const isCat = get(p, 'selected.listGroup') === 'category';
              const tier = get(p, 'assessment.data.realm.tier');
              const scard = get(p, `scorecards.${p.scorecardIndex}`);
              const scTier = get(scard, `edition.realm.tier`);
              const hasDocs = !!get(scard, 'edition.config.docs');
              return hasDocs && isCat && tier !== 'builder' && !['division', undefined].includes(scTier);
            },
            onChange: (val) => {
              filterLocal = { ...filterLocal, value: 'any', useLocation: val };
              setFilterLocal((f) => ({ ...f, value: 'any', useLocation: val }))
            },
          },
        ]
        : [
          { val: 'value', type: 'select', options },
          { text: 'Hide N/A', val: 'filterLocal.hideNa', type: 'switch', onChange: (val) => setFilterLocal((f) => ({ ...f, value: 'any', hideNa: val })) },
          {
            text: 'Use Location',
            val: 'filterLocal.useLocation',
            type: 'switch',
            show: (p) => {
              const isCat = get(p, 'selected.listGroup') === 'category';
              const tier = get(p, 'assessment.data.realm.tier');
              const scard = get(p, `scorecards.${p.scorecardIndex}`);
              const scTier = get(scard, `edition.realm.tier`);
              const hasDocs = !!get(scard, 'edition.config.docs');
              return hasDocs && isCat && tier !== 'builder' && !['division', undefined].includes(scTier);
            },
            onChange: (val) => {
              filterLocal = { ...filterLocal, value: 'any', useLocation: val };
              setFilterLocal((f) => ({ ...f, value: 'any', useLocation: val }))
            },
          },
        ]
    );
  };

  const runFilter = async (d = scoredata) => {
    setFilterLoading(true);
    const { hasChildren } = await filterData(d, { ...filterLocal, filterHook, activeSc, search: searchString, activeEdit: activeEdit, assessment: assessment, assessmentTasks });
    setFilter(filterLocal);
    setFilterLoading(false);

    if (!size(d) && !size(hasChildren)) { return } //prevent a constant loop https://ibacos.atlassian.net/browse/PFM-1963

    setTreedProcessedScoreData(hasChildren);
  };

  return (isCourse ? <></> : <Filters
    {...{
      printView: props.printView,
      isTemplate: props.isTemplate,
      assessment: props.assessment,
      buttons: filterBtns,
      setFilter: setFilterLocal,
      selected: filterLocal,
      loading: filterLoading || !size(filter),
      filterLocal,
      changeSearch: setSearchString,
      searchString: searchString,
      activeEdit: activeEdit,
      defaultFilters,

      processedOverallScores,
      scorecards,
      setScorecardIndex,
      scorecardIndex,
      scores: props.scores,

      taskBanner: props.taskBanner,
    }}
  />);
}